import { createActions } from 'reduxsauce';
import { ProcessingRedirect } from 'store/donatePage/types';

export interface RecipientsState {
  data: RecipientsData;
  isLoading: boolean;
  error: string;
}

export type RecipientsData = {
  recipients: IRecipient[];
};

export type CampaignDetails = {
  campaign_website?: string;
  website?: string;
};

export type Competitor = {
  // not yet in response, needed for hvp
  competitor_name: string;
  raised: string;
  coh: string;
  is_incumbent: boolean;
};

export type MiscIds = {
  chariot_connect_id?: string;
  photo_id?: string;
  ext_de_url_id?: string;
  contribution_limit?: string;
  org_summary?: string;
  org_tag?: string;
};

export type Election = {
  display_info?: string;
  election_goal: string;
  state: string;
  projected_cost: string;
  contribution_limit: string;
  is_employer_address_required: boolean;
  ratings: Array<string | null>;
  pvi: string;
  competitor?: Competitor; // not yet in response, needed for hvp
  primary_election_date?: string; // not yet in response, needed for hvp
};

export type Race = {
  is_incumbent: boolean;
  is_self_funded: boolean;
  is_pay_to_play_issue: boolean;
  amount_raised: string;
  coh?: string; // not yet in response, needed for hvp
  financials_updated_at?: string; // not yet in response, needed for hvp
  campaign_details: CampaignDetails;
  election: Election;
};

export type IRecipient = {
  recipient_id: string;
  de_id: string;
  stripe_id?: string;
  misc_ids?: MiscIds;
  recipient_type: string;
  de_status: string;
  stripe_status: string;
  impact_score: string;
  recipient_name?: string;
  recipient_status?: string;
  race?: Race;
};

export type IRecipientSearchResult = {
  recipient: IRecipient;
  query_metadata: {
    match_score: number | null;
  };
};

export enum IRecipientIdEnum {
  DE_ID = 'de_id',
  STRIPE_ID = 'stripe_id',
  EXT_DE_URL_ID = 'ext_de_url_id',
  OATH_ID = 'recipient_id',
}

export enum RecipientsRequestType {
  donate_page = 'donate_page',
  donor_search = 'donor_search',
  admin = 'admin',
  other = 'other',
  checkout = 'checkout',
}

export enum RecipientStatus {
  IN_SESSION_RESTRICTED = 'IN_SESSION_RESTRICTED',
  WITHDREW = 'WITHDREW',
  LOST = 'LOST',
  COMPETITIVE_PRIMARY = 'COMPETITIVE_PRIMARY',
  ACTIVE = 'ACTIVE',
}

export enum DonorSearchSource {
  oath_donate_search = 'oath_donate_search',
  oath_cart_search = 'oath_cart_search',
  client_cart_search = 'client_cart_search',
}

export const RESTRICTED_RECIPIENT_STATUSES = [
  RecipientStatus.IN_SESSION_RESTRICTED,
  RecipientStatus.WITHDREW,
  RecipientStatus.LOST,
] as const;

export type RestrictedRecipientStatus = (typeof RESTRICTED_RECIPIENT_STATUSES)[number];

export const isRestrictedStatus = (status?: string): status is RestrictedRecipientStatus =>
  (RESTRICTED_RECIPIENT_STATUSES as readonly string[]).includes(status ?? '');

export type getRecipientProps = {
  amount: number;
  filter_ids?: string[];
  search_str?: string;
  is_public_searchable?: boolean;
  recipient_ids?: string[];
  order_by?: string;
  payment_processor?: ProcessingRedirect;
  request_type?: RecipientsRequestType;
  request_location_path: string;
  page_query_str?: string | null;
};

export interface RecipientsTypes {
  RECIPIENTS: 'RECIPIENTS';
  RECIPIENTS_START: 'RECIPIENTS_START';
  RECIPIENTS_SUCCESS: 'RECIPIENTS_SUCCESS';
  RECIPIENTS_ERROR: 'RECIPIENTS_ERROR';
  RECIPIENTS_CLEAN_UP: 'RECIPIENTS_CLEAN_UP';
}

const { Types, Creators } = createActions<RecipientsTypes>({
  recipients: ['data'],
  recipientsStart: null,
  recipientsSuccess: ['data'],
  recipientsError: ['error'],
  recipientsCleanUp: null,
});

export { Types };

export default Creators;
